import AddIcon from "@mui/icons-material/Add";
import { FilterOptionsState, FormHelperTextProps } from "@mui/material";
import { AutocompleteChangeReason, AutocompleteCloseReason, AutocompleteProps, AutocompleteRenderGroupParams, createFilterOptions } from "@mui/material/Autocomplete";
import Popper, { PopperProps } from "@mui/material/Popper";
import { styled } from "@mui/material/styles";
import clsx from "clsx";
import { Box, FlexColumn, FlexRow } from "designSystem/index";
import { useLocale } from "designSystem/locale";
import { largeListThreshold } from "designSystem/settings";
import { black, green, grey, red, white } from "designSystem/styles/color";
import { textFieldInnerOutlineStyle, textfieldStyle } from "designSystem/styles/style-functions";
import { FONT_BOLD, FONT_SEMIBOLD } from "designSystem/theme";
import { BLANK_COMPONENTITEM, ComponentItem } from "designSystem/types/component-item-types";
import { TooltipPlacement } from "designSystem/types/tooltip-types";
import { CSI_DROPDOWN_PLACEHOLDER, NONE_COMPONENT_ID, componentItemGroupByDefaultFn, getComponentItemDisabled, isComponentItemEqualToValue } from "designSystem/utilities/component-item-functions";
import React, { HTMLAttributes, ReactElement, ReactNode, SyntheticEvent, forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ButtonProps } from "../button/Button";
import FlexRowCenter from "../flex/FlexRowCenter";
import CsiImage, { CsiImageProps } from "../image/CsiImage";
import OverflowText from "../text/OverflowText";
import Text from "../text/Text";
import OverflowTextField from "../textfield/OverflowTextField";
import Autocomplete, { POPPER_MAX_WIDTH } from "./AutoCompleteList";
import InlineTextField from "./InlineTextField";
import ReactVirtuosoListboxComponent from "./ReactVirtuosoListboxComponent";
import ReactWindowListboxComponent, { ReactWindowListboxComponentProps } from "./ReactWindowListboxComponent";
import { generatePinyinCode } from "./SelectHelper";
import { IconButtonProps } from "@mui/material/IconButton";

const PREFIX = "SingleSelect";
const classes = {
	addIcon: `${PREFIX}addIcon`,
	customItem: `${PREFIX}customItem`,
	inputComponent: `${PREFIX}inputComponent`,
	invalidBorder: `${PREFIX}invalidBorder`,
	innerBorderOutline: `${PREFIX}innerBorderOutline`,
	optionGeneral: `${PREFIX}optionGeneral`,
	optionGroupLabel: `${PREFIX}optionGroupLabel`,
	optionGroupSuggested: `${PREFIX}optionGroupSuggested`,
	optionInvalid: `${PREFIX}optionInvalid`,
};

interface SingleSelectIconButtonProps extends IconButtonProps {
	/** Allow for putting data-csi-automation tag on the icon button */
	"data-csi-automation"?: string;
  }

export interface SingleSelectProps<T> extends Omit<Partial<AutocompleteProps<T, boolean, boolean, boolean>>, "onChange" | "limitTags"> {
	/** Allow custom options - false by default */
	allowCustomOptions?: boolean;

	/** indicate whether or not enable Pinyin code, false by default */
	allowPinyin?: boolean;

	/** override standard renderOption */
	customRenderOption?: (props: HTMLAttributes<HTMLLIElement>, option: ComponentItem, { selected }) => ReactNode;

	/** data-csi-automation label for component */
	dataCsiAutomation?: string;

	/** delayed request made search and has results */
	delayedRequestHasResults?: boolean;

	/** drop down placeholder */
	DropdownPlaceholder?: ReactNode;

	/** number of items to be loaded, the rest of items can be searched, 200 is default */
	filterLimit?: number;

	FormHelperTextProps?: Partial<FormHelperTextProps<"p", {}>>;

	/** Group the options by option.group - false by default */
	grouped?: boolean;

	/** help message or error message when validation fails */
	helperText?: string;

	/** help message resource or error message resource when validation fails */
	helperTextRes?: string;

	/** parameters to use for helper text resource */
	helperTextResParams?: Array<number | string>;

	/** image options to display  */
	ImageProps?: Omit<CsiImageProps, "src">;

	/** include a blank option in the dropdown */
	includeBlankOption?: boolean;

	/** Indicates whether the single select should be inline and scale to its natural width */
	inline?: boolean;

	/** autofocus for input */
	inputAutoFocus?: boolean;

	/** selectOnFocus for input */
	inputSelectOnFocus?: boolean;

	/** indicates if error should happen to the input element */
	invalid?: boolean;

	/** label for this component */
	label?: string;

	/** localization resource to use to lookup text; can be simple resource or type resource (i.e. "User.Name") */
	labelRes?: string;

	/** parameters to pass if looking up resource */
	labelResParams?: Array<number | string>;

	/** the name for the input element */
	name?: string;

	/** prevent autocomplete input toggle by mouse and keyboard clicks (useful for inline editing)*/
	noInputToggle?: boolean;

	/** editing cancelled */
	onCancel?: () => void;

	/** callback for value change */
	onChange?: (event: React.ChangeEvent<{}>, value: ComponentItem, reason: AutocompleteChangeReason) => void;

	/** callback on dropdown open */
	onOpenCallback?: () => Promise<void>;

	/** options for the drop down */
	options: T[];

	/** place holder for the TextField component */
	placeHolder?: string;

	/** localization resource to use to lookup text; can be simple resource or type resource (i.e. "User.Name") */
	placeHolderRes?: string;

	/** parameters to pass if looking up resource */
	placeHolderResParams?: Array<number | string>;

	/** Indicates whether to set the width of popper component to fit the content */
	popperFitContentWidth?: boolean;

	/** indicate if this attribute is required or not */
	required?: boolean;

	/** actions to display as sticky at bottom of option list */
	selectActions?: ReactElement<ButtonProps>[];

	/* indicate slim card rendering, attribute "name2" will be used for second attribute rendering  */
	slimCard?: boolean;

	/* indicate if the slim card should have the second attribute in the value  */
	slimCardInPopupOnly?: boolean;

	/** groups to mark as suggested */
	suggestedGroups?: string[];

	/** placement for tooltip of textbox, default value is bottom-start */
	tooltipPlacement?: TooltipPlacement;

	/** use React Virtuoso instead of React Window when the list gets big (over 200 items) */
	preferReactVirtuoso?: boolean;

	/** value */
	value?: T;
}

const StyledPopper = styled(Popper)(({
	theme,
}) => ({
	[`& .${classes.addIcon}`]: {
		paddingLeft: 2,
	},
	[`& .${classes.customItem}`]: {
		fontStyle: "italic",
	},
	[`& .${classes.optionGeneral}`]: {
		margin: 0,
		paddingLeft: 0,
	},
	[`& .${classes.optionGroupLabel}`]: {
		alignItems: "center",
		backgroundColor: white[50],
		display: "flex",
		height: 26,
		lineHeight: "26px",
		paddingLeft: 8,
		position: "sticky",
		top: -4,

		"& :first-of-type": {
			fontWeight: FONT_BOLD,
		},
	},
	[`& .${classes.optionGroupSuggested}`]: {
		backgroundColor: green[100],
		borderRadius: 3,
		color: green[700],
		display: "inline-block",
		fontWeight: FONT_SEMIBOLD,
		height: 18,
		lineHeight: "9px",
		marginLeft: 6,
		padding: 4,
	},
	[`& .${classes.optionInvalid}`]: {
		color: red[600],
		display: "inline-block",
		flexGrow: 1,
		flexShrink: 0,
		fontStyle: "italic",
		textAlign: "right",
	},
}));

const textFieldStyling = (theme) => {
	return {
		[`& .${classes.inputComponent}`]: textfieldStyle(theme),
		[`& .${classes.innerBorderOutline}`]: textFieldInnerOutlineStyle(theme),
	};
};

const StyledInlineTextField = styled(InlineTextField)(({
	theme,
}) => (textFieldStyling(theme)));

const StyledOverflowTextField = styled(OverflowTextField)(({
	theme,
}) => (textFieldStyling(theme)));

const StyledPlaceholder = styled("li")(() => ({
	marginTop: -4,
	marginBottom: 4,
}));

const StyledAutocomplete = styled(Autocomplete)(({
	theme,
}) => ({
	[`&.${classes.invalidBorder}`]: {
		"& .MuiInputBase-root": {
			borderColor: `${red[600]} !important`,
		},
	},
})) as typeof Autocomplete;

// TODO: needs a fix - Generic Type is not working with forwardRef
//  https://stackoverflow.com/questions/58469229/react-with-typescript-generics-while-using-react-forwardref
const SingleSelect = forwardRef(<T extends ComponentItem>(props: SingleSelectProps<T>, ref) => {
	const {
		allowCustomOptions = false,
		allowPinyin = false,
		customRenderOption,
		dataCsiAutomation,
		delayedRequestHasResults,
		DropdownPlaceholder,
		filterLimit,
		FormHelperTextProps,
		grouped = false,
		helperText,
		helperTextRes,
		helperTextResParams,
		ImageProps,
		includeBlankOption,
		inline,
		inputAutoFocus,
		inputSelectOnFocus,
		inputValue,
		invalid,
		label,
		labelRes,
		labelResParams,
		ListboxComponent,
		name,
		noInputToggle,
		onBlur,
		open,
		options,
		onCancel,
		onChange,
		onOpenCallback,
		placeHolder,
		placeHolderRes,
		placeHolderResParams,
		popperFitContentWidth,
		preferReactVirtuoso,
		selectActions = null,
		slimCard,
		slimCardInPopupOnly,
		suggestedGroups,
		value,
		required,
		tooltipPlacement = "bottom-start",
		...otherProps
	} = props;

	const {
		addIcon,
		customItem,
		invalidBorder,
		inputComponent,
		innerBorderOutline,
		optionGeneral,
		optionGroupLabel,
		optionGroupSuggested,
		optionInvalid,
	} = classes;

	const { getComponentItemName, getComponentItemSlimName, resource } = useLocale();
	const getOptionLabel = slimCard && !slimCardInPopupOnly ? getComponentItemSlimName : getComponentItemName;
	const suggestedGroupsSet = new Set(suggestedGroups);

	const text = placeHolderRes ? resource(placeHolderRes, placeHolderResParams) : null;
	const [showOptionsList, setShowOptionsList] = useState<boolean>(open || false);
	const pinyinIsGenerated = useRef<boolean>(false);
	const propLabel = label || (labelRes && resource(labelRes, labelResParams));

	let updatedAndClosing = false;

	const [_value, setValue] = useState<ComponentItem>();

	// use React Window or React Virtuoso library (ListboxComponent) to render large lists
	let listBoxComponent = ListboxComponent;
	const isLargeList = options.length > largeListThreshold;
	if (isLargeList) {
		listBoxComponent = preferReactVirtuoso
			? ReactVirtuosoListboxComponent
			: React.forwardRef((props: ReactWindowListboxComponentProps, ref: React.ForwardedRef<HTMLDivElement>) => (
				<ReactWindowListboxComponent
					{...props}
					isSlimCard={slimCard}
					largeListRef={ref}
				/>
			));
	}

	// property "open" will control component initial open state. If "open" is not specified, default component behavior will be controlled by "showOptionslist"
	const showOptions = open ?? showOptionsList;

	useEffect(() => {
		if (value !== _value) {
			setValue(value);
		}
	}, [value]);

	const addDelayedSearchPlaceholder = (options: ComponentItem[]) => {
		options.unshift({
			id: CSI_DROPDOWN_PLACEHOLDER,
			disabled: true,
		});
	};

	// handler for filter options
	const filter = useCallback(createFilterOptions<ComponentItem>({
		limit: filterLimit || 5000,
		stringify: allowPinyin ? (option) => option.pinyinCode : null,
	}), [filterLimit]);

	function slimCardFilterOptions<T extends ComponentItem>(options: T[], params: FilterOptionsState<T>): ComponentItem[] {
		const inputValueRegExp = new RegExp(params.inputValue, "i");
		return options.filter((item) => getComponentItemName(item)
			?.match(inputValueRegExp) || item.name2?.match(inputValueRegExp));
	}

	// Handle the custom options
	function filterOptions<T extends ComponentItem>(options: T[], params: FilterOptionsState<T>): ComponentItem[] {
		if (allowPinyin && !pinyinIsGenerated.current) {
			generatePinyinCode<T>(options);
			pinyinIsGenerated.current = true;
		}

		const filtered = slimCard ? slimCardFilterOptions(options, params) : filter(options, params);

		if (allowCustomOptions) {
			const { inputValue: originalInputValue } = params;
			const inputValue = originalInputValue.trim();

			if (inputValue !== "") {
				// always add the custom one to the top
				filtered.unshift({
					customOption: true,
					id: inputValue,
					name: inputValue,
					value: inputValue,
				});
			}
		}

		if (DropdownPlaceholder) {
			addDelayedSearchPlaceholder(filtered);
		}

		return filtered;
	}

	// callback for closing drop-down
	function onCloseDropdown(event: SyntheticEvent<Element, Event>, reason: AutocompleteCloseReason): void {
		// don't close dropdown when click on input control
		if (noInputToggle && (reason === "toggleInput") && ((event.target as Element).tagName === "INPUT")) {
			return;
		}
		// when value changes, callback onChangeValue() call setShowOptionsList(false); which causing 
		// dropdown to close and in turn call onCloseDropdown(). 
		// This all happen in one function scope and other hooks are not yet updated.
		// This happens when dropdown being closed via blur or clicking drop down arrow.
		// But if dropdown would be closing after value change, then this will be false and onCancel will not be called
		if (!updatedAndClosing) {
			onCancel?.();
		}
		setShowOptionsList(false);
	}

	// callback for opening the drop-down
	async function onOpenDropdown(): Promise<void> {
		await onOpenCallback?.();
		setShowOptionsList(true);
	}

	const groupBy = grouped ? componentItemGroupByDefaultFn : null;

	// update the selected value and callback as needed
	function onChangeValue<T extends ComponentItem>(event: SyntheticEvent<Element, Event>, newValue: T, reason: AutocompleteChangeReason): void {
		const newValueItem = newValue as ComponentItem;
		setValue(newValueItem);
		onChange?.(event, newValueItem, reason);
		// not using hook for updatedAndClosing because setShowOptionsList(false) will close dropdown and immediately call onCloseDropdown
		// hook will not be updated in this case, while scope variable will 
		updatedAndClosing = true;
		setShowOptionsList(false);
	};

	const singleSelectPopperMemo = useMemo(() => {
		return function singleSelectPopper(props: PopperProps): JSX.Element {
			let { style } = props;

			if (inline && !style?.width) {
				style = { ...style };
				style.width = "fit-content";
			} else if (popperFitContentWidth && style?.width) {
				style.minWidth = style.width;
				style.width = "fit-content";
				style.maxWidth = POPPER_MAX_WIDTH;
			}

			return (
				<StyledPopper
					{...props}
					style={style}
				/>
			);
		};
	}, [inline, popperFitContentWidth]);

	const renderGroupMemo = useMemo(() => {
		return function renderGroup(params: AutocompleteRenderGroupParams): ReactNode {
			let { children } = params;
			if (!Array.isArray(children)) {
				children = null;
			}
			const attributes: any = {};
			attributes.key = params.key;
			if (isLargeList && children) {
				attributes["data-csi-child"] = children;
			}
			return (
				<li {...attributes}>
					{!!params.group && (
						<div className={optionGroupLabel}>
							<OverflowText text={params.group} />
							{suggestedGroupsSet?.has(params.group) &&
								<Text className={optionGroupSuggested} res="SuggestedUpperCase" />
							}
						</div>
					)}
					<ul className={optionGeneral}>
						{params.children}
					</ul>
				</li>
			);
		};
	}, [suggestedGroups]);

	const textFieldHelperText = helperText ?? resource(helperTextRes, helperTextResParams);

	const renderInputMemo = useMemo(() => {
		return function renderInput({ InputProps, ...params }): ReactNode {
			return (
				inline ? (
					<StyledInlineTextField
						FormHelperTextProps={FormHelperTextProps}
						autoFocus={inputAutoFocus}
						dataCsiAutomation={`dropdown-${dataCsiAutomation}`}
						error={invalid}
						name={name}
						{...params}
						InputProps={{
							classes: {
								notchedOutline: innerBorderOutline,
								root: inputComponent,
							},
							...InputProps,
						}}
						helperText={textFieldHelperText}
						label={propLabel}
						placeholder={text || placeHolder}
						selectOnFocus={inputSelectOnFocus}
					/>
				)
					: (
						<StyledOverflowTextField
							FormHelperTextProps={FormHelperTextProps}
							autoFocus={inputAutoFocus}
							dataCsiAutomation={`dropdown-${dataCsiAutomation}`}
							error={invalid}
							name={name}
							{...params}
							InputProps={{
								classes: {
									notchedOutline: innerBorderOutline,
									root: inputComponent,
								},
								...InputProps,
							}}
							helperText={textFieldHelperText}
							label={propLabel}
							placeholder={text || placeHolder}
							selectOnFocus={inputSelectOnFocus}
							tooltipPlacement={tooltipPlacement}
						/>
					)
			);
		};
	}, [FormHelperTextProps, inline, helperText, helperTextRes, placeHolder, text]);

	function renderOption<T extends ComponentItem>(props: HTMLAttributes<HTMLLIElement>, option: T, { selected }): ReactNode {
		const { className, ...otherProps } = props;
		const { customOption, id, invalid, name2, imageSrc } = option;
		const text = getComponentItemName(option);
		const isOptionNone = id === NONE_COMPONENT_ID;
		const isOptionNoneSelected = isOptionNone && (_value == undefined);
		const optionClassName = clsx(
			className,
			slimCard && text && "autocomplete-option-slim",
			isOptionNone && "autocomplete-singleselect-option-none",
			isOptionNoneSelected && "area-selected",
		);

		const optionAutomation = dataCsiAutomation ? "dropdown-" + dataCsiAutomation + "-Option" + props["data-option-index"] : undefined;

		if (id === CSI_DROPDOWN_PLACEHOLDER) {
			return (
				<StyledPlaceholder key={id}>
					{DropdownPlaceholder}
				</StyledPlaceholder>
			);
		}
		return (
			<li
				className={optionClassName}
				data-csi-automation={optionAutomation}
				data-csi-value={option.value}
				key={id}
				{...otherProps}
			>
				<FlexRowCenter gap={1} width={1}>
					{
						ImageProps && (
							<Box
								border={1}
								borderColor={grey[400]}
								borderRadius={1}
								flexShrink={0}
								height={32}
								width={32}
							>
								<CsiImage
									src={imageSrc}
									{...ImageProps}
								/>
							</Box>
						)
					}
					<FlexColumn width="100%">
						<FlexRow>
							{/* render the custom option as italic words */}
							<OverflowText
								className={customOption ? customItem : null}
								color={black[100]}
								text={text}
								tooltipProps={{ placement: "right" }}
							/>
							{customOption &&
								<AddIcon className={addIcon} />
							}
							{invalid &&
								<Text className={optionInvalid} res="Invalid" />
							}
						</FlexRow>
						{slimCard && text &&
							<OverflowText text={name2} />
						}
					</FlexColumn>
				</FlexRowCenter>
			</li>
		);
	}

	// autocomplete clear input value on blur by default, so we have to clear ref value as well
	const _onBlur = (ev) => {
		onBlur?.(ev);
		// C8-62452: call "onCancel" if onBlur is not defined and dropdown list is not opened
		if (!onBlur && !showOptionsList) {
			onCancel?.();
		}
	};

	const errorClassName = invalid ? invalidBorder : "";

	const className = `${errorClassName}`;

	return (
		<StyledAutocomplete<ComponentItem, boolean, boolean, boolean>
			ListboxComponent={listBoxComponent}
			PopperComponent={singleSelectPopperMemo}
			blurOnSelect={false}
			className={className}
			clearOnEscape={!required}
			componentsProps={{
				popupIndicator:{
					"data-csi-automation": `dropdown-Chevron-${dataCsiAutomation}`,
				} as SingleSelectIconButtonProps,
			}}
			data-csi-automation="SingleSelect"
			disableClearable={required || !_value?.id}
			filterOptions={filterOptions}
			forcePopupIcon
			getOptionDisabled={getComponentItemDisabled}
			getOptionLabel={getOptionLabel}
			groupBy={groupBy}
			isOptionEqualToValue={isComponentItemEqualToValue}
			onBlur={_onBlur}
			onChange={onChangeValue}
			onClose={onCloseDropdown}
			onOpen={onOpenDropdown}
			open={showOptions}
			options={(includeBlankOption) ? [BLANK_COMPONENTITEM, ...options] : options}
			ref={ref}
			renderGroup={renderGroupMemo}
			renderInput={renderInputMemo}
			renderOption={customRenderOption || renderOption}
			selectActions={(!DropdownPlaceholder || delayedRequestHasResults) && selectActions}
			value={_value || null}
			{...otherProps}
		/>
	);
});

export default SingleSelect;